import React, { useRef, useState } from 'react';
import { Upload, Loader2, Camera, X } from 'lucide-react';
import { uploadFile } from '../../services/filesService';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * Image upload component - two usage modes:
 *
 * Mode 1 - OCR/business card (legacy):
 * @param {Function} props.onImageUpload - Callback when image is selected (receives event)
 * @param {boolean} props.isProcessing - Whether OCR is processing
 * @param {string} props.imagePreview - Preview image URL (base64 or full URL)
 * @param {Object} props.translations - Translation object with uploadTitle, uploadClick, uploadCardText, analyzing
 *
 * Mode 2 - Generic image upload:
 * @param {string} props.value - Current image path (stored path)
 * @param {Function} props.onChange - Callback when image path changes (receives filePath string)
 * @param {string} props.entityType - Entity type for upload path (e.g. "expenses", "places")
 * @param {React.ReactNode} props.placeholder - Placeholder icon/content when no image
 * @param {string} props.className - Additional CSS classes for the container
 */
const ImageUpload = (props) => {
    // Mode 1: legacy OCR mode
    if (props.translations !== undefined || props.onImageUpload !== undefined) {
        const { onImageUpload, isProcessing, imagePreview, translations = {} } = props;
        return (
            <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {translations.uploadTitle}
                </label>
                <div className="relative">
                    <input
                        type="file"
                        accept="image/*"
                        onChange={onImageUpload}
                        className="hidden"
                        id="card-upload"
                        disabled={isProcessing}
                    />
                    <label
                        htmlFor="card-upload"
                        className={`flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${isProcessing
                            ? 'border-gray-300 bg-gray-50'
                            : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'
                            }`}
                    >
                        {imagePreview ? (
                            <img
                                src={imagePreview}
                                alt="Business card preview"
                                className="h-full object-contain"
                            />
                        ) : isProcessing ? (
                            <div className="flex flex-col items-center">
                                <Loader2 className="w-12 h-12 text-cabaza-pink animate-spin mb-2" />
                                <span className="text-sm text-gray-600">{translations.analyzing}</span>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center px-4">
                                <Upload className="w-12 h-12 text-cabaza-purple mb-2" />
                                <span className="text-sm text-gray-600 text-center">
                                    {translations.uploadClick}<br />
                                    {translations.uploadCardText}
                                </span>
                            </div>
                        )}
                    </label>
                </div>
            </div>
        );
    }

    // Mode 2: generic image upload
    const { value, onChange, entityType = 'files', placeholder, className = '' } = props;
    const [isUploading, setIsUploading] = useState(false);
    const inputRef = useRef(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const result = await uploadFile(file, { path: `${entityType}/`, entityType });
            const data = result?.data ?? result?.DATA ?? {};
            const filePath = data.filePath ?? data.FILEPATH ?? data.filepath ?? '';
            if (filePath && onChange) {
                onChange(filePath);
            }
        } catch (err) {
            console.error('ImageUpload error:', err);
        } finally {
            setIsUploading(false);
        }
    };

    const handleClear = (e) => {
        e.stopPropagation();
        if (onChange) onChange('');
        if (inputRef.current) inputRef.current.value = '';
    };

    const imageUrl = value ? `${FILES_BASE_URL}/${value}` : null;

    return (
        <div
            className={`relative flex items-center justify-center border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 overflow-hidden cursor-pointer hover:border-gray-400 hover:bg-gray-100 transition-colors ${className}`}
            style={{ width: '100%', height: '100%', minWidth: 64, minHeight: 64 }}
            onClick={() => !isUploading && inputRef.current?.click()}
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
                disabled={isUploading}
            />

            {isUploading ? (
                <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            ) : imageUrl ? (
                <>
                    <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                    >
                        <X className="w-3 h-3 text-white" />
                    </button>
                </>
            ) : (
                <div className="flex flex-col items-center gap-1 p-2">
                    {placeholder ? (
                        typeof placeholder === 'string' ? (
                            <>
                                <Camera className="w-6 h-6 text-gray-400" />
                                <span className="text-xs text-gray-400 text-center">{placeholder}</span>
                            </>
                        ) : placeholder
                    ) : (
                        <Camera className="w-6 h-6 text-gray-400" />
                    )}
                </div>
            )}
        </div>
    );
};

export default ImageUpload;
